import React, { useRef, useState, useEffect, useMemo, Suspense, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ProductHero from '@stone/components/product/ProductHero';
import { ProductActionTypes } from '@common/state/features/common.types';
import analytics from '@stone/utils/analytics';
import Metatags from '@components/common/Metatags';
import { getProductAvailability } from '@utils/product';
import JsonLd from '@common/utils/JsonLd';
import { useParams } from 'react-router-dom';
import Breadcrumbs from '@components/listing/Breadcrumb';
import api from '@stone/utils/api';
import StoryListing from '../storylisting/StoryListing';
import Details from '../details/DetailsLoader';
import ProductDetailsDialog from './ProductDetailsDialog';
import ProductRecommendation from './ProductRecommendation';
import ProductShopTheLook from './ProductShopTheLook';
import customEventBus from '@common/scripts/libs/eventBus';
const ProductInstalmentDrawer = React.lazy(() => import('./ProductInstalmentDrawer'));
// const TestRecent = () => (
// ...
// Removed very long comment - recover through git if needed
const ProductPage = (props) => {
    var _a, _b;
    let { pid } = useParams();
    // * STATES ----------------------------------------------------------------------------------------------------------------------------------------
    const [media, setMedia] = useState([]);
    const [previousPid, setpreviousPid] = useState('');
    const [previousPid2, setpreviousPid2] = useState('');
    const [heroImageLoaded, setHeroImageLoaded] = useState(window.ssr ? true : false);
    const [sizeguideOpen, setSizeguideOpen] = useState(false);
    const [editorials, setEditorials] = useState(null);
    const [currentProduct, setCurrentProduct] = useState();
    // *  REDUX ----------------------------------------------------------------------------------------------------------------------------------------
    const dispatch = useDispatch();
    const current = useSelector((state) => state.product).current;
    // *  FUNCTIONS ----------------------------------------------------------------------------------------------------------------------------------------
    const sendAnalyticsEvents = () => {
        // composing subCategory as per OE-5541
        window.datalayerdata = Object.assign(Object.assign({}, window.datalayerdata), { pageCategory: 'pdp' });
        analytics.sendGeneralVariables();
    };
    const refreshPDP = (pid, sendAnalytics) => {
        dispatch({
            type: ProductActionTypes.FETCH_PRODUCT_AND_USER,
            payload: {
                pid,
                sendAnalytics,
            },
        });
    };
    const getProductSchema = () => {
        var _a, _b, _c, _d, _e;
        return {
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: current === null || current === void 0 ? void 0 : current.productName,
            image: (_a = current.imgs) === null || _a === void 0 ? void 0 : _a.urls,
            description: current === null || current === void 0 ? void 0 : current.shortDescription,
            brand: {
                '@type': 'Brand',
                name: 'Stone Island',
            },
            offers: {
                '@type': 'Offer',
                url: location.href,
                priceCurrency: (_c = (_b = current === null || current === void 0 ? void 0 : current.price) === null || _b === void 0 ? void 0 : _b.sales) === null || _c === void 0 ? void 0 : _c.currency,
                price: (_e = (_d = current === null || current === void 0 ? void 0 : current.price) === null || _d === void 0 ? void 0 : _d.sales) === null || _e === void 0 ? void 0 : _e.value,
                itemCondition: 'https://schema.org/NewCondition',
                availability: getProductAvailability(current),
            },
        };
    };
    const getBreadcrumbSchemaData = () => {
        return {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: current.breadcrumbs.map((item, index) => ({
                '@type': 'ListItem',
                position: ++index,
                name: item.htmlValue,
                item: location.origin + item.url,
            })),
        };
    };
    // handle load or refresh of the PDP
    // * USE MEMO ----------------------------------------------------------------------------------------------------------------------------------------
    const productSchemaData = useMemo(() => (current ? getProductSchema() : undefined), [current]);
    const breadcrumbSchemaData = useMemo(() => { var _a; return (((_a = current === null || current === void 0 ? void 0 : current.breadcrumbs) === null || _a === void 0 ? void 0 : _a.length) > 0 ? getBreadcrumbSchemaData() : undefined); }, [current]);
    const looks = useMemo(() => {
        var _a;
        return (_a = current === null || current === void 0 ? void 0 : current.lookProducts) === null || _a === void 0 ? void 0 : _a.filter((l) => l !== null);
    }, [current]);
    //* USE EFFECT ----------------------------------------------------------------------------------------------------------------------------------------
    const firstShow = useRef(true);
    const editorialPageId = useRef(null);
    //Send analitycs on first render
    useEffect(() => {
        if (firstShow.current && current) {
            sendAnalyticsEvents();
            analytics.productDetailPage(current);
        }
        firstShow.current = false;
        return () => {
            firstShow.current = true;
        };
    }, []);
    useEffect(() => {
        if (current && pid) {
            if (previousPid.includes(current.masterId) && pid.includes(current.masterId)) {
                // Color change inside PDP
                refreshPDP(pid, 'detail');
            }
            else if (current.id != pid) {
                // al back del browser
                // PDP <-> PDP
                refreshPDP(pid, 'withTransitions');
            }
        }
        else if (pid) {
            // Refresh/landing directily on PDP
            // console.info('no current - Refresh/landing directily on PDP');
            refreshPDP(pid, 'detail');
        }
        setpreviousPid(pid);
    }, [pid]);
    // scroll top 0 nella navigazione tra PDP
    useEffect(() => {
        if (current && previousPid2 != '' && pid && previousPid2 != pid) {
            sendAnalyticsEvents();
            customEventBus.emit('header:reset', () => { });
            customEventBus.emit('refresh:recommender', { handle: ['einstein-product-recommendations', 'einstein-product-recommendations-2'] });
            if (!firstShow.current) {
                window.scrollTo(0, 0);
            }
        }
        setpreviousPid2(pid);
    }, [current, pid]);
    //Handle media
    useEffect(() => {
        var _a, _b, _c;
        if (((_a = current === null || current === void 0 ? void 0 : current.imgs) === null || _a === void 0 ? void 0 : _a.urls) && ((_b = current === null || current === void 0 ? void 0 : current.imgs) === null || _b === void 0 ? void 0 : _b.urls.length) > 0) {
            let newMedia = (_c = current === null || current === void 0 ? void 0 : current.imgs) === null || _c === void 0 ? void 0 : _c.urls.map((el) => el);
            // exclude F duplicate of R on standard PDP
            newMedia = newMedia.filter((el) => !el.includes('_X'));
            if (current === null || current === void 0 ? void 0 : current.videos) {
                for (const [key, value] of Object.entries(current === null || current === void 0 ? void 0 : current.videos)) {
                    newMedia.splice(parseInt(key), 0, value);
                }
            }
            setMedia(newMedia.map((el) => (el.startsWith('palette_') ? el.substring(8) : el)));
            handleImageLoaded();
        }
    }, [(_a = current === null || current === void 0 ? void 0 : current.imgs) === null || _a === void 0 ? void 0 : _a.urls, (_b = current === null || current === void 0 ? void 0 : current.mbm) === null || _b === void 0 ? void 0 : _b.palettes]);
    const firstLoad = useRef(true);
    useEffect(() => {
        if (firstLoad.current && current) {
            // window.addEventListener('message', function (message) {
            //   if (message.data && message.data.event_type === 'spottImpression') {
            //     analytics.hostsposts('hotspots view', current.masterId);
            //   }
            //   if (message.data && message.data.event_type === 'spottInteraction') {
            //     analytics.hostsposts('hotspots hover', current.masterId);
            //   }
            // });
            // fallback per image loaded
            // sendProductAnalytics();
            setTimeout(() => {
                if (!heroImageLoaded) {
                    setHeroImageLoaded(true);
                }
            }, 3000);
            firstLoad.current = false;
        }
    }, [current]);
    useEffect(() => {
        if (current) {
            setCurrentProduct(current);
        }
    }, [current]);
    //get editorials for pdp
    useEffect(() => {
        if (current) {
            let loadpage = !editorialPageId.current || editorialPageId.current != (current.editorialPage || 'pdp-default');
            // window.devmode && console.log(current.editorialPage, editorialPageId.current, 'LOAD:', loadpage);
            if (loadpage) {
                editorialPageId.current = current.editorialPage || 'pdp-default';
                api.getPdp(current.id, editorialPageId.current).then((res) => {
                    var _a;
                    let _editorials;
                    if (!res.error && res.page) {
                        let regions = ((_a = res.page) === null || _a === void 0 ? void 0 : _a.regions) && Object.keys(res.page.regions);
                        if (regions) {
                            regions.forEach((region, _i) => {
                                var _a;
                                if ((_a = res.page.regions[region].components) === null || _a === void 0 ? void 0 : _a.length) {
                                    _editorials = _editorials || {};
                                    if (region.indexOf('drawer') == 0) {
                                        _editorials.drawer = [...(_editorials.drawer || []), ...res.page.regions[region].components];
                                    }
                                    else {
                                        _editorials[region] = res.page.regions[region];
                                    }
                                }
                            });
                        }
                    }
                    if (_editorials) {
                        setEditorials(_editorials);
                    }
                });
            }
        }
        else {
            //reset editorials if missing (on change page with transitions)
            setEditorials(null);
        }
    }, [current]);
    // handle image loaded to start animations
    const handleImageLoaded = () => {
        const image = document.querySelector('.product-image img');
        // console.info('immagine: ', image);
        if (!image) {
            setHeroImageLoaded(true);
            return;
        }
        image.addEventListener('load', (event) => {
            // let isLoaded = image.complete && image.naturalHeight !== 0;
            // console.log('image loaded');
            setHeroImageLoaded(true);
        });
    };
    const storyListing = useMemo(() => {
        var _a, _b, _c, _d;
        if ((_a = editorials === null || editorials === void 0 ? void 0 : editorials.storylisting) === null || _a === void 0 ? void 0 : _a.components) {
            const story_listing = (_b = editorials === null || editorials === void 0 ? void 0 : editorials.storylisting) === null || _b === void 0 ? void 0 : _b.components.find((com) => com.typeid === 'component_story_listing');
            if (!!((_d = (_c = story_listing === null || story_listing === void 0 ? void 0 : story_listing.regions) === null || _c === void 0 ? void 0 : _c.stories) === null || _d === void 0 ? void 0 : _d.components.length)) {
                return story_listing;
            }
            else {
                return null;
            }
        }
        else {
            return null;
        }
    }, [editorials]);
    const componentDetails = useMemo(() => {
        var _a, _b;
        if ((_a = editorials === null || editorials === void 0 ? void 0 : editorials.details) === null || _a === void 0 ? void 0 : _a.components) {
            const details = (_b = editorials === null || editorials === void 0 ? void 0 : editorials.details) === null || _b === void 0 ? void 0 : _b.components.find((com) => com.typeid === 'component_details');
            const accordion = [...details === null || details === void 0 ? void 0 : details.regions.accordion.components];
            delete details.regions;
            return Object.assign({ type: (accordion && 'accordion') || 'content', accordion: accordion }, details);
        }
        else {
            return null;
        }
    }, [editorials]);
    const componentsDrawer = useMemo(() => {
        return editorials === null || editorials === void 0 ? void 0 : editorials.drawer;
    }, [editorials]);
    const instalmentpayments = useMemo(() => {
        return editorials === null || editorials === void 0 ? void 0 : editorials.instalmentpayments;
    }, [editorials]);
    const onRenderDetails = useCallback(() => {
        customEventBus.emit('pdp:details', {});
    }, []);
    return (React.createElement(React.Fragment, null, currentProduct && (React.createElement("div", null,
        React.createElement(Metatags, { pageMetaTags: current === null || current === void 0 ? void 0 : current.pageMetaTags, meta: current === null || current === void 0 ? void 0 : current.meta }),
        productSchemaData && React.createElement(JsonLd, { data: productSchemaData }),
        breadcrumbSchemaData && React.createElement(JsonLd, { data: breadcrumbSchemaData }),
        React.createElement(ProductHero, { product: currentProduct, media: media, size: props.size, contactformurl: props.contactformurl, deliveryurl: props.deliveryurl, ordermgrurl: props.ordermgrurl, aftercareurl: props.aftercareurl, heroImageLoaded: heroImageLoaded, setSizeguideOpen: (open) => {
                setSizeguideOpen(open);
            }, sizeguideOpen: sizeguideOpen, anchorToScroll: "product-content", isPdp: props.isProduct }),
        React.createElement("div", { className: "product-content", id: "product-content" },
            componentDetails && (React.createElement("div", { ref: onRenderDetails },
                React.createElement("div", { id: "pdp-details-in" }),
                React.createElement(Details, Object.assign({}, componentDetails)),
                React.createElement("div", { id: "pdp-details-out" }))),
            (looks === null || looks === void 0 ? void 0 : looks.length) > 0 && React.createElement(ProductShopTheLook, { product: current, looks: looks }),
            (current === null || current === void 0 ? void 0 : current.id) && React.createElement(ProductRecommendation, { customClasses: "pdp-recommendation", product: current, slot: "einstein-product-recommendations" }),
            (current === null || current === void 0 ? void 0 : current.id) && React.createElement(ProductRecommendation, { customClasses: "pdp-recommendation", product: current, slot: "einstein-product-recommendations-2" }),
            React.createElement(Breadcrumbs, { product: currentProduct })),
        storyListing && (React.createElement("div", { className: "product-stories " },
            React.createElement(StoryListing, { title: storyListing.title, stories: storyListing.regions.stories.components }))),
        React.createElement(ProductDetailsDialog, { product: currentProduct, componentsDrawer: componentsDrawer }),
        React.createElement(Suspense, { fallback: React.createElement(React.Fragment, null) }, instalmentpayments && React.createElement(ProductInstalmentDrawer, { product: currentProduct, componentsDrawer: instalmentpayments }))))));
};
export default ProductPage;
